import { For, Show, createEffect, on } from "solid-js"
import { TextShimmer } from "@opencode-ai/ui/text-shimmer"
import { useLanguage } from "@/context/language"
import type { ReviewPresentation } from "@/cowork/review-presentation"

const verdictKeys = {
  ready: "cowork.enterprise.reports.status.readyForReview",
  revise: "cowork.enterprise.workflows.reporting.stage.needs_review",
  qualified: "cowork.enterprise.reports.status.draft",
  blocked: "cowork.enterprise.reports.status.blocked",
} as const

export function CoworkReviewResult(props: {
  review: ReviewPresentation
  partID: string
  onContentRendered?: () => void
}) {
  const language = useLanguage()
  const result = () => (props.review.status === "complete" ? props.review : undefined)
  createEffect(on(() => props.review, () => props.onContentRendered?.()))

  return (
    <div data-component="review-result" data-timeline-part-id={props.partID}>
      <Show
        when={result()}
        fallback={
          <div role="status">
            <TextShimmer text={language.t("cowork.timeline.activity.reviewInformation")} />
          </div>
        }
      >
        {(review) => (
          <section class="space-y-4">
            <p class="font-medium">{language.t(verdictKeys[review().verdict])}</p>
            <p class="whitespace-pre-wrap break-words">{review().summary}</p>
            <Show when={review().findings.length}>
              <ul class="list-disc space-y-4 pl-5">
                <For each={review().findings}>
                  {(finding) => (
                    <li class="space-y-1 whitespace-pre-wrap break-words">
                      <Show when={finding.location}>
                        <p class="font-medium">{finding.location}</p>
                      </Show>
                      <p>{finding.message}</p>
                      <Show when={finding.suggestedFix}>
                        <p>{finding.suggestedFix}</p>
                      </Show>
                    </li>
                  )}
                </For>
              </ul>
            </Show>
            <Show when={review().limitations.length}>
              <div class="space-y-2">
                <p class="font-medium">{language.t("cowork.enterprise.workflows.reporting.findings.warning")}</p>
                <ul class="list-disc space-y-2 pl-5">
                  <For each={review().limitations}>
                    {(limitation) => <li class="whitespace-pre-wrap break-words">{limitation}</li>}
                  </For>
                </ul>
              </div>
            </Show>
          </section>
        )}
      </Show>
    </div>
  )
}
